import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSystemTables } from '../../hooks/UseSystemTables';
import Order from '../../models/Order';
import { approveOrder, updateTransportCompany, rejectOrder } from '../../services/ApiService';
import ShipmentsPopup from '../ShipmentsPopup/ShipmentsPopup';
import styles from './OrderCard.module.css';

import { useTheme } from '@mui/material/styles';
import {
  Card,
  Box,
  CardContent,
  Collapse,
  Typography,
  Grid,
  Chip,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';

interface OrderCardProps {
  order: Order;
}

const OrderCard: React.FC<OrderCardProps> = ({ order }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const queryClient = useQueryClient();

  // Local states
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedTransportCompany, setSelectedTransportCompany] = useState<string>(
    order.transportationCompany || ''
  );
  const [currentStatus, setCurrentStatus] = useState<number>(order.status);
  const [isDeliveriesOpen, setIsDeliveriesOpen] = useState(false);

  // Mutations
  const approveMutation = useMutation({
    mutationFn: async () => await approveOrder(order.orderId),
    onSuccess: (data) => {
      setCurrentStatus(data.status);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: () => {
      alert(t('OrderCard.Messages.ApproveError'));
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => await rejectOrder(order.orderId),
    onSuccess: (data) => {
      setCurrentStatus(data.status);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: () => {
      alert(t('OrderCard.Messages.RejectError'));
    },
  });

  const updateTransportMutation = useMutation<
    { updatedTransport: string },
    Error,
    string
  >({
    mutationFn: async (newTransport: string) =>
      await updateTransportCompany(order.orderId, newTransport),
    onSuccess: (data) => {
      setSelectedTransportCompany(data.updatedTransport);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: () => {
      alert(t('OrderCard.Messages.UpdateTransportCompanyError'));
    },
  });

  // Get system tables data
  const { data: systemTablesData } = useSystemTables();

  // Find related data from system tables
  const client = systemTablesData?.data?.clients?.find(
    (client) => client.clientInnerId === order.clientInnerId
  );
  const clientName = client ? client.clientName : 'Client not found';

  const status = systemTablesData?.data?.orderStatus?.find(
    (status) => status._id === currentStatus
  );
  const statusNameDisplay = i18n.language === 'he' ? status?.name_hebrew : status?.name;

  const location = systemTablesData?.data?.warehouseLocations?.find(
    (loc) => loc._id == order.origin
  );
  const locationName = i18n.language === 'he' ? location?.nameHeb : location?.nameEng;

  const transportCompanies = systemTablesData?.data?.transportCompanies || [];

  // Map status codes to keys (assumes theme.palette.orderStatus exists)
  const statusKeyMapping: { [key: number]: keyof typeof theme.palette.orderStatus } = {
    1: 'pending',
    2: 'approved',
    3: 'processing',
    4: 'shipped',
    5: 'delivered',
    6: 'canceled',
    7: 'onHold',
    8: 'rejected',
    9: 'awaitingPickup',
  };
  
  const statusKey: keyof typeof theme.palette.orderStatus = statusKeyMapping[order.status];
  const statusColors = theme.palette.orderStatus[statusKey];

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  const formatDateTime = (date: Date) =>
    new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'short',
      timeStyle: 'short',
      hourCycle: 'h23',
    }).format(date);

  const handleTransportChange = (event: SelectChangeEvent<string>) => {
    const newTransport = event.target.value;
    setSelectedTransportCompany(newTransport);
    updateTransportMutation.mutate(newTransport);
  };

  const handleApprove = () => {
    approveMutation.mutate();
  };

  const handleReject = () => {
    rejectMutation.mutate();
  };

  const handleDeliveriesClick = () => {
    setIsDeliveriesOpen(true);
  };

  const handleDeliveriesClose = () => {
    setIsDeliveriesOpen(false);
  };

  const handleDuplicateOrder = () => {
    // Prepare the order data for the new order form, excluding dates
    const orderData = {
      itemNum: order.itemNum,
      origin: order.origin,
      amount: order.amount.toString(),
      amountRestriction: !!order.tonAmount, // If tonAmount exists, set amountRestriction to true
      isSelfShipping: order.isSelfShipping,
      clientInnerId: order.clientInnerId,
      tonAmount: order.tonAmount?.toString() || '',
      transportationCompany: order.transportationCompany,
      comment: order.comment || '',
      // Exclude requestedTime to let user set a new date
    };

    navigate('/create-order', { state: { preFilledData: orderData } });
  };

  const handleEditOrder = () => {
    // Helper function to convert Date to datetime-local format (YYYY-MM-DDTHH:mm)
    const formatDateForInput = (date: Date | undefined): string => {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    // Prepare the order data for editing, including requestedTime
    const orderData = {
      itemNum: order.itemNum,
      origin: order.origin,
      amount: order.amount.toString(),
      amountRestriction: !!order.tonAmount, // If tonAmount exists, set amountRestriction to true
      isSelfShipping: order.isSelfShipping,
      clientInnerId: order.clientInnerId,
      tonAmount: order.tonAmount?.toString() || '',
      transportationCompany: order.transportationCompany,
      comment: order.comment || '',
      requestedTime: formatDateForInput(order.requestedTime),
    };

    navigate('/create-order', { state: { preFilledData: orderData, isEdit: true, orderId: order.orderId } });
  };

  return (
    <Card sx={{ marginBottom: 2 }}>
      {/* Card Header */}
      <Box className={styles.cardHeader}>
        <Box className={styles.headerContent}>
          <Box>
            <Typography variant="h6">{order.orderId}</Typography>
            <Typography variant="subtitle2">{clientName}</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Typography variant="body2">
              {t('Order.RequestedTime')}:{" "}
              {order.requestedTime ? formatDateTime(new Date(order.requestedTime)) : 'N/A'}
            </Typography>
            <Typography variant="body2">
              {t('Order.Amount')}: {order.amount}
            </Typography>
            <Chip
              label={statusNameDisplay}
              sx={{
                backgroundColor: statusColors.main,
                color: statusColors.contrastText,
                fontWeight: 'bold',
                borderRadius: '12px',
                padding: '0.3rem 0.8rem',
                fontSize: '0.9rem',
              }}
            />
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={toggleExpand}
          aria-label="expand"
          sx={{ color: 'text.primary', p: 0, minWidth: 0, width: 24, height: 24 }}
        >
          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>
      {/* Collapsible Details */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <CardContent sx={{ backgroundColor: 'white' }}>
          <Grid container spacing={2}>
            {/* Shipping Details */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                {t('OrderCard.Titles.ShippingDetails')}
              </Typography>
              {!order.isSelfShipping ? (
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id={`transport-select-label-${order.orderId}`}>
                    {t('Order.TransportationCompany')}
                  </InputLabel>
                  <Select
                    labelId={`transport-select-label-${order.orderId}`}
                    id={`transport-select-${order.orderId}`}
                    value={selectedTransportCompany}
                    onChange={handleTransportChange}
                    label={t('Order.TransportationCompany')}
                    disabled={updateTransportMutation.isPending}
                  >
                    {transportCompanies.map((company: any) => (
                      <MenuItem key={company.companyInnerId} value={company.companyInnerId}>
                        {company.companyName ? company.companyName : company.companyInnerId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2">
                  {t('Order.TransportationCompany')}: {order.transportationCompany || '-'}
                </Typography>
              )}
              <Typography variant="body2" mt={1}>
                {t('Order.ShippingCertificateNumber')}: {order.shippingCertificateNumber}
              </Typography>
            </Grid>
            {/* Time Details */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                {t('OrderCard.Titles.TimeDetails')}
              </Typography>
              <Typography variant="body2">
                {t('Order.ExpectedReceiveTime')}:{" "}
                {order.expectedReceiveTime ? formatDateTime(new Date(order.expectedReceiveTime)) : 'N/A'}
              </Typography>
              <Typography variant="body2">
                {t('Order.OrderCreationTime')}:{" "}
                {order.orderCreationTime ? formatDateTime(new Date(order.orderCreationTime)) : 'N/A'}
              </Typography>
              <Typography variant="body2">
                {t('Order.RequestedTime')}:{" "}
                {order.requestedTime ? formatDateTime(new Date(order.requestedTime)) : 'N/A'}
              </Typography>
            </Grid>
            {/* Order Details */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                {t('OrderCard.Titles.OrderDetails')}
              </Typography>
              <Typography variant="body2">
                {t('Order.CustomerNum')}: {order.clientInnerId}
              </Typography>
              <Typography variant="body2">
                {t('OrderCard.Labels.ClientName')}: {clientName}
              </Typography>
              <Typography variant="body2">
                {t('Order.ItemNum')}: {order.itemNum}
              </Typography>
              <Typography variant="body2">
                {t('Order.Origin')}: {locationName}
              </Typography>
              <Typography variant="body2">
                {t('Order.Amount')}: {order.amount}
              </Typography>
              {order.tonAmount && (
                <Typography variant="body2">
                  {t('Order.TonAmount')}: {order.tonAmount}
                </Typography>
              )}
              <Typography variant="body2">
                {t('Order.IsSelfShipping.Title')}:{" "}
                {order.isSelfShipping ? t('Order.IsSelfShipping.Yes') : t('Order.IsSelfShipping.No')}
              </Typography>
              {order.comment && (
                <Typography variant="body2">
                  {t('Order.Comment')}: {order.comment}
                </Typography>
              )}
            </Grid>
          </Grid>
          {/* Action Buttons (shown when status is pending) */}
          {currentStatus === 1 && (
            <Box display="flex" justifyContent="flex-end" mt={2} gap={2}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.success.main,
                  color: theme.palette.success.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.success.dark,
                  },
                }}
                onClick={handleApprove}
                disabled={approveMutation.isPending}
              >
                {approveMutation.isPending ? (
                  <CircularProgress size={24} />
                ) : (
                  t('OrderCard.Labels.Approve')
                )}
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.error.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.error.dark,
                  },
                }}
                onClick={handleReject}
                disabled={rejectMutation.isPending}
              >
                {rejectMutation.isPending ? (
                  <CircularProgress size={24} />
                ) : (
                  t('OrderCard.Labels.Reject')
                )}
              </Button>
            </Box>
          )}
          {/* Action Buttons */}
          <Box mt={2} display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            {/* Edit Order Button */}
            <Button
              variant="contained"
              startIcon={i18n.language === 'he' ? undefined : <EditIcon />}
              endIcon={i18n.language === 'he' ? <EditIcon /> : undefined}
              onClick={handleEditOrder}
              sx={{ 
                minWidth: 150,
                backgroundColor: 'var(--button-bg)',
                color: 'var(--button-text)',
                '&:hover': {
                  backgroundColor: '#cfdad1',
                },
                ...(i18n.language === 'he' && {
                  '& .MuiButton-endIcon': {
                    marginRight: '8px',
                  },
                }),
              }}
            >
              {t('OrderCard.EditOrder')}
            </Button>

            {/* Duplicate Order Button */}
            <Button
              variant="contained"
              startIcon={i18n.language === 'he' ? undefined : <ContentCopyIcon />}
              endIcon={i18n.language === 'he' ? <ContentCopyIcon /> : undefined}
              onClick={handleDuplicateOrder}
              sx={{ 
                minWidth: 150,
                backgroundColor: 'var(--button-bg)',
                color: 'var(--button-text)',
                '&:hover': {
                  backgroundColor: '#cfdad1',
                },
                ...(i18n.language === 'he' && {
                  '& .MuiButton-endIcon': {
                    marginRight: '8px',
                  },
                }),
              }}
            >
              {t('OrderCard.DuplicateOrder')}
            </Button>
            
            {/* Shipments Button */}
            {order.shipments && order.shipments.length > 0 && (
              <Button
                variant="contained"
                startIcon={i18n.language === 'he' ? undefined : <LocalShippingIcon />}
                endIcon={i18n.language === 'he' ? <LocalShippingIcon /> : undefined}
                onClick={handleDeliveriesClick}
                sx={{ 
                  minWidth: 200,
                  backgroundColor: 'var(--button-bg)',
                  color: 'var(--button-text)',
                  '&:hover': {
                    backgroundColor: '#cfdad1',
                  },
                  ...(i18n.language === 'he' && {
                    '& .MuiButton-endIcon': {
                      marginRight: '8px',
                    },
                  }),
                }}
              >
                {t('OrderCard.Labels.ViewDeliveries')} ({order.shipments.length})
              </Button>
            )}
          </Box>
        </CardContent>
      </Collapse>
      {/* Shipments Popup */}
      {order.shipments && order.shipments.length > 0 && (
        <ShipmentsPopup
          open={isDeliveriesOpen}
          onClose={handleDeliveriesClose}
          shipments={order.shipments}
          orderId={order.orderId}
        />
      )}
    </Card>
  );
};

export default OrderCard;